import { useCallback } from "react";
import { useDependencyInjection } from "../../config/di/dependency_injection_context";

import CommonConstants from "../constants";

/**
 * useDropDownLoader
 */

export const useDropDownLoader = () => {
  const {
    commons: {
      getCentroCustoDropDownUseCase,
      getClientesDropDownUseCase,
      getFornecedorDropDownUseCase,
      getFuncionariosDropDownUseCase,
      getFuncionariosByIdCentroCustoDropDownUseCase,
      getCargosDropDownUseCase,
      getSubcontasDropDownUseCase,
      getOrdemComprasDropDownUseCase,
      getFuncionariosTercDropDownUseCase,
      getProdutosServicosDropDownUseCase,
      getRequisicaoCompraDropDownUseCase,
      getColetaPrecoDropDownUseCase,
      getClienteCentroCustoDropDownUseCase,
    },
  } = useDependencyInjection();

  // Centro de Custo
  const loadCentroCustoDropDown = useCachedFunction({
    key: CommonConstants.cacheKey.centroCustoDropDown,
    loader: async () => await getCentroCustoDropDownUseCase.getCentroCustoDropDown(),
  });

  // Clientes
  const loadClientesDropDown = useCachedFunction({
    key: CommonConstants.cacheKey.clientesDropDown,
    loader: async () => await getClientesDropDownUseCase.getClientesDropDown(),
  });

  // Fornecedores
  const loadFornecedoresDropDown = useCachedFunction({
    key: CommonConstants.cacheKey.fornecedoresDropDown,
    loader: async (filtrarCentroCusto?: boolean) => await getFornecedorDropDownUseCase.getFornecedorDropDown(filtrarCentroCusto),
  });

  // Funcionarios
  const loadFuncionariosDropDown = useCachedFunction({
    key: CommonConstants.cacheKey.funcionariosDropDown,
    loader: async (props?: { filtrarCentroCusto?: boolean, includeCantBeSelected?: boolean }) =>
      await getFuncionariosDropDownUseCase.getFuncionariosDropDown(props?.filtrarCentroCusto, props?.includeCantBeSelected),
  });

  // Funcionarios Por Id
  const loadFuncionariosByIdDropDown = useCachedFunction({
    loader: async (idCentroCusto: number) => await getFuncionariosByIdCentroCustoDropDownUseCase
      .getFuncionariosByIdCentroCustoDropDown(idCentroCusto, true),
  });

  // Cargos
  const loadCargosDropDown = useCachedFunction({
    key: CommonConstants.cacheKey.cargoDropDown,
    loader: async () => await getCargosDropDownUseCase.getCargosDropDown(),
  });

  // Bancos Pagamento
  const loadBancosPagamentoDropDown = useCachedFunction({
    key: CommonConstants.cacheKey.bancosPagamentoDropDown,
    loader: async () => await getCargosDropDownUseCase.getCargosDropDown(),
  });

  // SubConta
  const loadSubContasDropDown = useCachedFunction({
    key: CommonConstants.cacheKey.subContasDropDown,
    loader: async () => await getSubcontasDropDownUseCase
      .getSubcontasDropDown(),
  });

  // Ordem de Compra
  const loadOrdemComprasDropDown = useCachedFunction({
    key: CommonConstants.cacheKey.ordemComprasDropDown,
    loader: async () => await getOrdemComprasDropDownUseCase
      .getOrdemComprasDropDown(),
  });

  // Usuario
  const loadUsuariosDropDown = useCachedFunction({
    key: CommonConstants.cacheKey.usuariosDropDown,
    loader: async () => await getSubcontasDropDownUseCase
      .getSubcontasDropDown(),
  });

  // Funcionarios Terc
  const loadFuncionariosTercDropDown = useCachedFunction({
    key: CommonConstants.cacheKey.funcionariosTercDropDown,
    loader: async (filtrarCentroCusto?: boolean) => await getFuncionariosTercDropDownUseCase
      .getFuncionariosTercDropDown(
        filtrarCentroCusto,
      ),
  });

  // Produtos/Servicos
  const loadProdutoServicosDropDown = useCachedFunction({
    key: CommonConstants.cacheKey.produtosServicosDropDown,
    loader: async ({ filtrarTipo, incluirIdProdutoServico, incluirCodigoDescricao }: { filtrarTipo?: string, incluirIdProdutoServico?: number, incluirCodigoDescricao?: boolean }) =>
      await getProdutosServicosDropDownUseCase.getProdutosServicosDropDown(filtrarTipo, incluirIdProdutoServico, incluirCodigoDescricao),
  });

  // Requisicao Compra
  const loadRequisicaoCompraDropDown = useCachedFunction({
    key: CommonConstants.cacheKey.requisicaoCompraDropDown,
    loader: async (filtrarAprovados?: boolean) => await getRequisicaoCompraDropDownUseCase.getRequisicaoCompraDropDown(filtrarAprovados),
  });

  // Coleta preco
  const loadColetaPrecoDropDown = useCachedFunction({
    key: CommonConstants.cacheKey.coletaPrecoDropDown,
    loader: async () => await getColetaPrecoDropDownUseCase.getColetaPrecoDropDown(),
  });

  // Cliente Centro Custo
  const loadClienteCentroCustoDropDown = useCachedFunction({
    key: CommonConstants.cacheKey.clienteCentroCustoDropDown,
    loader: async () => await getClienteCentroCustoDropDownUseCase.getClienteCentroCustoDropDown(),
  });

  return {
    loadCentroCustoDropDown,
    loadClientesDropDown,
    loadFornecedoresDropDown,
    loadFuncionariosDropDown,
    loadFuncionariosByIdDropDown,
    loadCargosDropDown,
    loadBancosPagamentoDropDown,
    loadSubContasDropDown,
    loadOrdemComprasDropDown,
    loadUsuariosDropDown,
    loadFuncionariosTercDropDown,
    loadProdutoServicosDropDown,
    loadRequisicaoCompraDropDown,
    loadColetaPrecoDropDown,
    loadClienteCentroCustoDropDown,
  };
}

/**
 * Cache function
 */

type CachedFunctionProps<T extends {}, TArgs extends any[]> = {
  key?: string;
  loader: (...args: TArgs) => Promise<T>;
};

export const useCachedFunction = <T extends {}, TArgs extends any[],>({
  key,
  loader,
}: CachedFunctionProps<T, TArgs>) => {
  return useCallback(async (onSet: (props: T) => void, ...args: TArgs) => {
    if (!key) {
      onSet(await loader(...args));
    } else {
      const cachedValue = window.localStorage.getItem(key);
      if (typeof cachedValue === 'string') {
        try {
          onSet(JSON.parse(cachedValue) as T);

          const p = new Promise(async (accept, reject) => {
            try {
              const data = await loader(...args);
              window.localStorage.setItem(key, JSON.stringify(data));
              onSet(data);
              accept(null);
            } catch (e: unknown) {
              reject(e);
            }
          });

          p.catch(e => { throw e; });
        } catch (e: unknown) {
          try {
            const data = await loader(...args);
            window.localStorage.setItem(key, JSON.stringify(data));
            onSet(data);
          } catch (e: unknown) {
            throw e;
          }
        }
      } else {
        try {
          const data = await loader(...args);
          window.localStorage.setItem(key, JSON.stringify(data));
          onSet(data);
        } catch (e: unknown) {
          throw e;
        }
      }
    }
  }, [
    key,
    loader,
  ]);
}